/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Uri from 'jsuri';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import _ from 'underscore';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
// NOTE: this MUST be loaded and not imported because it BREAKS SSR
import createLoadableComponent from 'js/lib/createLoadableComponent';

import { Button, Grid, Typography, Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked, { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import type { Props } from 'bundles/verification/components/withJumioVerification';
import withJumioVerification from 'bundles/verification/components/withJumioVerification';
import {
  ALL_RETRIES_USED,
  APPROVED,
  NOT_STARTED,
  RETRY_LIMIT,
  getIdStateMessages,
  getNameVerificationMessages,
  getVerificationTitles,
} from 'bundles/verification/data/VerificationConstants';
import { getUserFullName } from 'bundles/verification/data/VerificationUtils';

import _t from 'i18n!nls/verification';

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<'a'>>(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Button
);

const PersonaVerificationModal = createLoadableComponent(
  () => import('bundles/verification/components/PersonaVerificationModal')
);

const styles = {
  fullName: css`
    padding-right: var(--cds-spacing-300);
  `,
  statusIcon: css`
    padding-right: var(--cds-spacing-50);
  `,
  ctaButtonRight: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,
  jumioNotice: css`
    padding-top: var(--cds-spacing-150);
    font-weight: 400;
    font-size: 12px;
    color: var(--cds-color-grey-700);
    line-height: 1.5;
  `,
};

type State = {
  isModalOpened: boolean;
  personaFlowCompleted: boolean; // temporary bypass until our system can set proper values
};

class JumioVerificationSettings extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof JumioVerificationSettings.contextTypes>;

  constructor(props: Props) {
    super(props);

    this.state = {
      isModalOpened: false,
      personaFlowCompleted: false,
    };
  }

  componentDidMount() {
    const { verificationType } = this.context.router.location.query;
    if (verificationType === 'id') this.setState({ isModalOpened: true });
  }

  getIdVerificationState() {
    const { userVerificationInformation, latestVerificationStatus } = this.props;
    const { verifiedBy, jumioIdVerificationAttemptHistory } = userVerificationInformation;
    const { failedAttemptCount } = !!jumioIdVerificationAttemptHistory && jumioIdVerificationAttemptHistory;

    if (failedAttemptCount === 0 && ['JUMIO', 'SSO_SIGNIN', 'SIGTRACK', 'ADMIN', 'PERSONA'].includes(verifiedBy)) {
      return APPROVED;
    }
    if (failedAttemptCount >= RETRY_LIMIT) {
      // Jumio specific consideration
      return ALL_RETRIES_USED;
    } else if (!latestVerificationStatus) {
      return NOT_STARTED;
    }
    return latestVerificationStatus.verificationStatus;
  }

  shouldRenderIdVerification() {
    const { verificationRequirements, userVerificationInformation } = this.props;
    const { verifiedBy } = userVerificationInformation;
    // Note - if user verifiedBy SSO_SIGNIN, we should show name verification
    // flow. So this should output false.
    return (
      verificationRequirements === 'NEEDS_TO_VERIFY_ID' ||
      (verificationRequirements === 'REQUIREMENT_MET' && ['JUMIO', 'ADMIN', 'SIGTRACK', 'PERSONA'].includes(verifiedBy))
    );
  }

  componentDidUpdate() {
    // Do this focus here because the button may not be rendered yet when closeModal function is called
    if (!this.state.isModalOpened) {
      const button = document.getElementById('id_verification_button') as HTMLButtonElement;
      button?.focus();
    }
  }

  closeModal = () => {
    this.setState({ isModalOpened: false });
    this.props.refetchVerificationStatus();
  };

  personaOverrideOnSuccess = () => {
    this.setState({
      personaFlowCompleted: true,
      isModalOpened: false,
    });
  };

  renderIdVerification() {
    const verificationState = this.getIdVerificationState();
    const fullName = getUserFullName(this.props.userVerificationInformation);
    const idStateMessages = getIdStateMessages(fullName);
    const contentDivContent = <p>{idStateMessages[verificationState].contentDivText}</p>;
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const { ctaDivText } = idStateMessages[verificationState];
    const ctaDiv = ctaDivText && this.renderCTA(ctaDivText);
    const contentDiv = (
      <div className="content-div">
        {this.state.isModalOpened && (
          <PersonaVerificationModal closeModal={this.closeModal} onSuccessFunc={this.personaOverrideOnSuccess} />
        )}
        {contentDivContent}
      </div>
    );

    const noticeDiv = ctaDivText && (
      <Typography2 component="p" variant="subtitleMedium" css={styles.jumioNotice} className="jumio-notice">
        <FormattedMessage
          message={_t(
            'Note: Upon clicking {ctaDivText}, you will be taken to a third party service "Persona" to securely complete the verification process. Your photo and ID will be immediately deleted after the verification is complete. If you need any assistance completing the ID verification, please visit our {helpCenter}'
          )}
          helpCenter={
            <a
              href="https://learner.coursera.help/hc/articles/209818953-Set-up-ID-verification"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline' }}
            >
              {_t('help center')}
            </a>
          }
          ctaDivText={ctaDivText}
        />
      </Typography2>
    );

    const ctaTitle = getVerificationTitles().ID_VERIFICATION_TITLE;

    return { ctaDiv, contentDiv, ctaTitle, noticeDiv };
  }

  // Non Persona specific
  renderNameAttestation() {
    const { userVerificationInformation } = this.props;
    const ctaTitle = getVerificationTitles().NAME_VERIFICATION_TITLE;
    const fullName = getUserFullName(userVerificationInformation);
    const nameVerificationMessages = getNameVerificationMessages(fullName);
    if (userVerificationInformation.verifiedBy !== 'NONE') {
      const { UPDATE_NAME } = nameVerificationMessages;
      return {
        contentDiv: <p>{UPDATE_NAME.contentDivText}</p>,
        ctaDiv: this.renderCTA(UPDATE_NAME.ctaDivText),
        ctaTitle,
        noticeDiv: null,
      };
    } else {
      const { VERIFY_NAME } = nameVerificationMessages;
      return {
        contentDiv: <p>{VERIFY_NAME.contentDivText}</p>,
        ctaDiv: this.renderCTA(VERIFY_NAME.ctaDivText),
        ctaTitle,
        noticeDiv: null,
      };
    }
  }

  renderCTA(ctaText: $TSFixMe) {
    const { personaFlowCompleted, isModalOpened } = this.state;
    if (this.shouldRenderIdVerification()) {
      return (
        <TrackedCdsButton
          id="id_verification_button"
          disabled={personaFlowCompleted}
          variant="secondary"
          trackingName="id_verification"
          trackingData={{
            client: 'persona',
            timestamp: Date.now(),
          }}
          onClick={() => this.setState({ isModalOpened: true })}
          aria-label={getVerificationTitles().ID_VERIFICATION_TITLE}
          loading={isModalOpened}
        >
          {ctaText}
        </TrackedCdsButton>
      );
    }

    const userAttestationPageUrl = new Uri().setPath('/user-verification');

    _(this.props.additionalQueryParams)
      .pairs()
      .forEach(([paramName, paramValue]) => userAttestationPageUrl.addQueryParam(paramName, paramValue));

    return (
      <TrackedLinkButton
        trackingName="name_attestation"
        variant="secondary"
        trackingData={{
          client: 'persona',
          timestamp: Date.now(),
        }}
        component="a"
        href={userAttestationPageUrl.toString()}
        target={this.props.openInNewTab ? '_blank' : undefined}
        rel={this.props.openInNewTab ? 'noopener noreferrer' : undefined}
      >
        {ctaText}
      </TrackedLinkButton>
    );
  }

  render() {
    const { showCTAonRight } = this.props;

    const { noticeDiv, contentDiv, ctaDiv, ctaTitle } = this.shouldRenderIdVerification()
      ? this.renderIdVerification()
      : this.renderNameAttestation();

    return (
      <div data-e2e="verification">
        {showCTAonRight ? (
          <div>
            <Grid container>
              <Grid item xs>
                <Typography variant="h2">{ctaTitle}</Typography>
                {contentDiv}
              </Grid>
              <Grid item xs="auto" css={styles.ctaButtonRight}>
                {ctaDiv}
              </Grid>
            </Grid>
            {noticeDiv}
          </div>
        ) : (
          <section>
            <Typography2 component="h2" variant="subtitleMedium">
              {ctaTitle}
            </Typography2>
            {contentDiv}
            {ctaDiv}
            {noticeDiv}
          </section>
        )}
      </div>
    );
  }
}

export default withJumioVerification(JumioVerificationSettings);

export const BaseComponent = JumioVerificationSettings;
